import { useState, useEffect, useCallback } from "react";
import { Upload, Music, ExternalLink, Trash2, Radio, FileAudio, Play, Clock, RefreshCw, ListMusic, CheckSquare, Square, ChevronDown, Search } from "lucide-react";
import { PlaylistManager } from "./PlaylistManager";
import { ScheduleEditor } from "./ScheduleEditor";
import { BACKEND_URL } from "../../context/AuthContext";

export const LibraryTab = ({ currentUser, authToken }) => {
  // Self-contained state
  const [libraryFiles, setLibraryFiles] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [azuracastLibrary, setAzuracastLibrary] = useState([]);
  const [azuracastLibraryLoading, setAzuracastLibraryLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [storageUsage, setStorageUsage] = useState(null);
  const [uploadingMusic, setUploadingMusic] = useState(false);
  const [uploadMusicProgress, setUploadMusicProgress] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [submitError, setSubmitError] = useState('');

  const [activeSection, setActiveSection] = useState("autodj");
  const [selectedTracks, setSelectedTracks] = useState(new Set());
  const [playlists, setPlaylists] = useState([]);
  const [showAssignDropdown, setShowAssignDropdown] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [assignMessage, setAssignMessage] = useState({ text: "", type: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [playlistFilter, setPlaylistFilter] = useState("all");

  const headers = useCallback(() => {
    const h = { 'Content-Type': 'application/json' };
    if (authToken) h['Authorization'] = `Bearer ${authToken}`;
    return h;
  }, [authToken]);

  const fetchLibraryFiles = useCallback(async () => {
    setLibraryLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/storage/files`, { credentials: 'omit', headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setLibraryFiles(data.files || []);
        setStorageUsage(data.storage || null);
      }
    } catch (err) { console.error('Failed to fetch library:', err); }
    finally { setLibraryLoading(false); }
  }, [headers]);

  const fetchAzuracastLibrary = useCallback(async () => {
    setAzuracastLibraryLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/station/music-library`, { credentials: 'omit', headers: headers() });
      if (res.ok) {
        const data = await res.json();
        // Backend returns { files: [...], total, page, per_page } - extract files array
        setAzuracastLibrary(data.files || data || []);
      }
    } catch (err) { console.error('Failed to fetch AzuraCast library:', err); }
    finally { setAzuracastLibraryLoading(false); }
  }, [headers]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploadProgress('Uploading...');
    try {
      const formData = new FormData(); formData.append('file', file);
      const h = {}; if (authToken) h['Authorization'] = `Bearer ${authToken}`;
      const res = await fetch(`${BACKEND_URL}/api/storage/upload`, { method: 'POST', headers: h, body: formData });
      if (res.ok) { setSubmitSuccess('File uploaded!'); fetchLibraryFiles(); setTimeout(() => setSubmitSuccess(''), 3000); }
      else { const err = await res.json(); setSubmitError(err.detail || 'Upload failed'); setTimeout(() => setSubmitError(''), 3000); }
    } catch { setSubmitError('Upload failed'); }
    finally { setUploadProgress(''); }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/storage/delete/${fileId}`, { method: 'DELETE', headers: headers() });
      if (res.ok) { setLibraryFiles(prev => prev.filter(f => (f.id || f.file_id) !== fileId)); }
    } catch {}
  };

  const handleUploadMusicToAzuraCast = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploadingMusic(true); setUploadMusicProgress('Uploading to station...');
    try {
      const formData = new FormData(); formData.append('file', file);
      const h = {}; if (authToken) h['Authorization'] = `Bearer ${authToken}`;
      const res = await fetch(`${BACKEND_URL}/api/station/upload-music`, { method: 'POST', headers: h, body: formData });
      if (res.ok) { setUploadMusicProgress('Upload complete!'); fetchAzuracastLibrary(); setTimeout(() => setUploadMusicProgress(''), 3000); }
      else { const err = await res.json(); setUploadMusicProgress(`Error: ${err.detail || 'Upload failed'}`); }
    } catch { setUploadMusicProgress('Error: Upload failed'); }
    finally { setUploadingMusic(false); }
  };

  useEffect(() => {
    if (activeSection === "autodj" && currentUser?.azuracast_station_id) {
      fetchAzuracastLibrary();
      fetchPlaylists();
    }
    if (activeSection === "storage") {
      fetchLibraryFiles();
    }
    setPlaylistFilter("all");
    setSearchQuery("");
  }, [activeSection]);

  const fetchPlaylists = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/station/playlists`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) setPlaylists(await res.json());
    } catch (e) { console.error(e); }
  };

  const toggleTrack = (uid) => {
    setSelectedTracks((prev) => {
      const next = new Set(prev);
      next.has(uid) ? next.delete(uid) : next.add(uid);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedTracks.size === azuracastLibrary.length) {
      setSelectedTracks(new Set());
    } else {
      setSelectedTracks(new Set(azuracastLibrary.map((t) => t.unique_id)));
    }
  };

  const assignToPlaylist = async (playlistId) => {
    if (selectedTracks.size === 0) return;
    setAssigning(true);
    setShowAssignDropdown(false);
    try {
      const res = await fetch(`${BACKEND_URL}/api/station/tracks/batch-assign`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ playlist_id: playlistId, track_ids: [...selectedTracks] }),
      });
      const data = await res.json();
      if (res.ok) {
        setAssignMessage({ text: data.message, type: "success" });
        setSelectedTracks(new Set());
        fetchAzuracastLibrary();
      } else {
        setAssignMessage({ text: data.detail || "Failed", type: "error" });
      }
    } catch (err) {
      setAssignMessage({ text: err.message, type: "error" });
    } finally {
      setAssigning(false);
      setTimeout(() => setAssignMessage({ text: "", type: "" }), 4000);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "--:--";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const filteredLibrary = (() => {
    let list = azuracastLibrary;
    if (playlistFilter === "unassigned") {
      list = list.filter((t) => !t.playlists || t.playlists.length === 0);
    } else if (playlistFilter !== "all") {
      list = list.filter((t) => (t.playlists || []).some((p) => String(p.id) === playlistFilter));
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter((t) =>
        (t.title || "").toLowerCase().includes(q) ||
        (t.artist || "").toLowerCase().includes(q) ||
        (t.album || "").toLowerCase().includes(q) ||
        (t.genre || "").toLowerCase().includes(q)
      );
    }
    return list;
  })();

  return (
    <div className="space-y-6" data-testid="library-tab">
      {/* Section Tabs */}
      <div className="flex items-center gap-1 bg-white/5 rounded-xl p-1">
        {[
          { id: "autodj", icon: Play, label: "AutoDJ Library" },
          { id: "playlists", icon: ListMusic, label: "Playlists" },
          { id: "schedule", icon: Clock, label: "Schedule" },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveSection(tab.id)}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition ${
              activeSection === tab.id
                ? "bg-purple-500 text-white"
                : "text-white/50 hover:text-white hover:bg-white/5"
            }`}
            data-testid={`library-${tab.id}-tab`}
          >
            <tab.icon className="w-4 h-4" /> {tab.label}
          </button>
        ))}
      </div>

      {/* ========== AutoDJ Section ========== */}
      {activeSection === "autodj" && (
        <div className="space-y-4">
          {/* Upload + Assign Bar */}
          <div className="flex items-center gap-3 flex-wrap">
            <label className={`${uploadingMusic ? 'opacity-50 pointer-events-none' : ''} bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-xl transition cursor-pointer flex items-center gap-2 text-sm font-medium`}>
              <Upload className="w-4 h-4" /> {uploadingMusic ? "Uploading..." : "Upload Music"}
              <input
                type="file" className="hidden"
                accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/flac,audio/x-m4a,audio/mp4"
                onChange={(e) => { handleUploadMusicToAzuraCast(e); setTimeout(() => fetchAzuracastLibrary(), 3000); }}
                disabled={uploadingMusic}
                data-testid="autodj-upload-input"
              />
            </label>

            {/* Assign to Playlist */}
            {selectedTracks.size > 0 && (
              <div className="relative">
                <button
                  onClick={() => setShowAssignDropdown(!showAssignDropdown)}
                  disabled={assigning}
                  className="bg-purple-500 hover:bg-purple-600 text-white px-5 py-2.5 rounded-xl text-sm font-medium transition flex items-center gap-2"
                  data-testid="assign-playlist-btn"
                >
                  {assigning ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ListMusic className="w-4 h-4" />}
                  Add {selectedTracks.size} to Playlist
                  <ChevronDown className="w-4 h-4" />
                </button>
                {showAssignDropdown && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-gray-900 border border-white/10 rounded-xl shadow-xl z-50 max-h-60 overflow-y-auto" data-testid="playlist-dropdown">
                    {playlists.filter(p => !p.is_jingle).map((p) => (
                      <button
                        key={p.id}
                        onClick={() => assignToPlaylist(p.id)}
                        className="w-full text-left px-4 py-2.5 hover:bg-white/10 text-white text-sm transition flex items-center justify-between"
                        data-testid={`assign-to-${p.id}`}
                      >
                        <span className="truncate">{p.name}</span>
                        <span className="text-white/30 text-xs flex-shrink-0 ml-2">{p.num_songs} tracks</span>
                      </button>
                    ))}
                    {playlists.filter(p => !p.is_jingle).length === 0 && (
                      <p className="px-4 py-3 text-white/40 text-sm">No playlists available</p>
                    )}
                  </div>
                )}
              </div>
            )}

            <span className="text-white/40 text-sm ml-auto">
              {searchQuery ? `${filteredLibrary.length} of ${azuracastLibrary.length}` : `${azuracastLibrary.length}`} tracks
            </span>
            <button onClick={fetchAzuracastLibrary} className="text-purple-400 hover:text-purple-300 text-sm flex items-center gap-1" data-testid="autodj-refresh">
              <RefreshCw className={`w-4 h-4 ${azuracastLibraryLoading ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>

          {uploadMusicProgress && (
            <div className={`px-4 py-2 rounded-xl text-sm ${
              uploadMusicProgress.startsWith("Error") ? "bg-red-500/20 border border-red-500/50 text-red-300"
                : uploadMusicProgress.startsWith("Uploaded") ? "bg-green-500/20 border border-green-500/50 text-green-300"
                : "bg-white/10 text-white/70"
            }`} data-testid="autodj-upload-status">{uploadMusicProgress}</div>
          )}

          {assignMessage.text && (
            <div className={`px-4 py-2 rounded-xl text-sm ${
              assignMessage.type === "success" ? "bg-green-500/20 border border-green-500/50 text-green-300" : "bg-red-500/20 border border-red-500/50 text-red-300"
            }`} data-testid="assign-message">{assignMessage.text}</div>
          )}

          {/* Search & Filter Bar */}
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                placeholder="Search by title, artist, album, or genre..."
                className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-white text-sm placeholder-white/30 focus:outline-none focus:border-purple-500 transition"
                data-testid="library-search-input"
              />
              {searchQuery && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 text-xs">
                  {filteredLibrary.length} result{filteredLibrary.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <select
              value={playlistFilter}
              onChange={(e) => { setPlaylistFilter(e.target.value); setCurrentPage(1); }}
              className="bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500 transition min-w-[180px] appearance-none cursor-pointer"
              style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%23888' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10z'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center" }}
              data-testid="playlist-filter-select"
            >
              <option value="all">All Playlists</option>
              <option value="unassigned">Unassigned</option>
              {playlists.filter(p => !p.is_jingle).map((p) => (
                <option key={p.id} value={String(p.id)}>{p.name} ({p.num_songs})</option>
              ))}
            </select>
          </div>

          {/* Track List */}
          <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
            {!currentUser?.azuracast_station_id ? (
              <div className="text-center py-8 px-6">
                <Radio className="w-12 h-12 text-white/20 mx-auto mb-3" />
                <p className="text-white/50">No station connected</p>
              </div>
            ) : azuracastLibraryLoading ? (
              <div className="text-center py-8"><Radio className="w-8 h-8 text-purple-400 animate-pulse mx-auto" /><p className="text-white/50 mt-2">Loading...</p></div>
            ) : filteredLibrary.length === 0 ? (
              <div className="text-center py-8" data-testid="autodj-empty">
                <Music className="w-12 h-12 text-white/20 mx-auto mb-3" />
                <p className="text-white/50">{searchQuery ? 'No tracks match your search' : 'No tracks in AutoDJ'}</p>
              </div>
            ) : (
              <div data-testid="autodj-track-list">
                {/* Header */}
                <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-white/5 text-white/40 text-xs uppercase tracking-wider border-b border-white/5">
                  <div className="col-span-1 flex items-center">
                    <button onClick={selectAll} className="p-0.5" data-testid="select-all-tracks">
                      {selectedTracks.size === azuracastLibrary.length
                        ? <CheckSquare className="w-4 h-4 text-purple-400" />
                        : <Square className="w-4 h-4 text-white/30" />}
                    </button>
                  </div>
                  <div className="col-span-4">Title</div>
                  <div className="col-span-3">Artist</div>
                  <div className="col-span-3">Playlist(s)</div>
                  <div className="col-span-1 text-right">Dur.</div>
                </div>
                {/* Rows */}
                {filteredLibrary.map((track, idx) => {
                  const isSelected = selectedTracks.has(track.unique_id);
                  return (
                    <div
                      key={track.unique_id || idx}
                      onClick={() => toggleTrack(track.unique_id)}
                      className={`grid grid-cols-12 gap-2 items-center px-4 py-2.5 transition cursor-pointer border-b border-white/[0.03] ${
                        isSelected ? "bg-purple-500/10" : "hover:bg-white/5"
                      }`}
                      data-testid={`autodj-track-${idx}`}
                    >
                      <div className="col-span-1">
                        {isSelected
                          ? <CheckSquare className="w-4 h-4 text-purple-400" />
                          : <Square className="w-4 h-4 text-white/20" />}
                      </div>
                      <div className="col-span-4 flex items-center gap-2 min-w-0">
                        <FileAudio className="w-4 h-4 text-orange-400 flex-shrink-0" />
                        <span className="text-white text-sm truncate">{track.title || "Unknown"}</span>
                      </div>
                      <div className="col-span-3">
                        <span className="text-white/60 text-sm truncate block">{track.artist || "Unknown"}</span>
                      </div>
                      <div className="col-span-3 flex flex-wrap gap-1">
                        {(track.playlists || []).slice(0, 2).map((p) => (
                          <span key={p.id} className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 truncate max-w-[80px]">{p.name}</span>
                        ))}
                        {(track.playlists || []).length > 2 && (
                          <span className="text-[10px] text-white/30">+{track.playlists.length - 2}</span>
                        )}
                        {(!track.playlists || track.playlists.length === 0) && (
                          <span className="text-white/20 text-[10px]">Unassigned</span>
                        )}
                      </div>
                      <div className="col-span-1 text-right text-white/40 text-xs">{formatDuration(track.length)}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========== Playlists Section ========== */}
      {activeSection === "playlists" && (
        <PlaylistManager authToken={authToken} currentUser={currentUser} />
      )}

      {/* ========== Schedule Section ========== */}
      {activeSection === "schedule" && (
        <ScheduleEditor authToken={authToken} currentUser={currentUser} />
      )}
    </div>
  );
};
